/**
 * layoutFans.ts — single canonical home for layout constants + fan helpers.
 *
 * Per plan-triad H10: this file owns ALL layout constants for the v0.2
 * topology-driven pipeline. topologyWalk / layoutDag / layoutGroups
 * IMPORT from here — never re-declare.
 *
 * Polar-arc helpers are pure functions returning positions (no
 * mutation, no Node typing). The existing chatDagLayout.ts retains
 * its own mutating layoutPolarArc until Phase 4 cutover; this is the
 * v0.2 design with cleaner separation between geometry and node IO.
 */
import type { Node } from 'reactflow';

export const CONSTANTS = {
  TURN_VERTICAL_GAP: 64,
  ROUND_VERTICAL_GAP: 22,
  ASST_PRELUDE_GAP: 56,
  TOOL_HORIZONTAL_GAP: 28,
  TOOL_VERTICAL_GAP: 20,
  TOOL_GUTTER: 56,
  PEER_ARC_SPAN_DEG: 120,
  PEER_POLAR_RADIUS_MIN: 180,
  PEER_POLAR_RADIUS_MAX: 400,
};

// v0.2 layout constants.
export const MAX_DEPTH = 10_000;
export const SPINE_FAN_COLLAPSE_THRESHOLD = 3;
export const PERSONA_COL_GAP = 720;
// Standby/companion Camp chips ride LEFT-side lanes off the x=0 spine (negated at
// the use site) — one lane = a 260-wide node + 60 gutter — incrementing per camp.
// Matches the C2.3 SWITCHBACK_LANE_DX so camp chips + switchback merges share a
// lane width. LEFT (not right): the ChatMap minimap owns the top-right corner.
export const CAMP_LANE_DX = 320;
export const INTER_CHAIN_GAP = CONSTANTS.TURN_VERTICAL_GAP;
export const LATERAL_OFFSET = CONSTANTS.TOOL_GUTTER;

/**
 * computeArcRadius — peer fan polar radius, scaled to avoid crashes
 * between peers when N is high.
 *
 * Clamped between PEER_POLAR_RADIUS_MIN and PEER_POLAR_RADIUS_MAX.
 * Formula approximates the chord length needed for the fan to span
 * its peer set without overlap, given the per-peer height and arc
 * span.
 */
export function computeArcRadius(N: number, peerH: number): number {
  if (N <= 1) return CONSTANTS.PEER_POLAR_RADIUS_MIN;
  const arcSpan = CONSTANTS.PEER_ARC_SPAN_DEG;
  return Math.max(
    CONSTANTS.PEER_POLAR_RADIUS_MIN,
    Math.min(
      CONSTANTS.PEER_POLAR_RADIUS_MAX,
      ((N - 1) * peerH * 0.6) / Math.sin((arcSpan / 2) * Math.PI / 180),
    ),
  );
}

/**
 * layoutPolarArc — LATERAL polar arc helper (right of parent).
 *
 * Pure: returns positions; does NOT mutate. Caller applies positions
 * to its node set. Used for consult_peers lateral fans in v0.2.
 *
 * NOT used for spine sibling fans — see layoutPolarArcDownward.
 */
export function layoutPolarArc(
  N: number, radius: number, anchorX: number, anchorY: number,
): Array<{ x: number; y: number }> {
  const arcSpan = CONSTANTS.PEER_ARC_SPAN_DEG;
  const startDeg = -arcSpan / 2;
  const positions: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < N; i++) {
    const t = N === 1 ? 0.5 : i / (N - 1);
    const deg = startDeg + t * arcSpan;
    const rad = (deg * Math.PI) / 180;
    positions.push({
      x: anchorX + radius * Math.cos(rad),
      y: anchorY + radius * Math.sin(rad),
    });
  }
  return positions;
}

/**
 * layoutPolarArcDownward — NEW (v0.2) — fans children DOWNWARD from a parent.
 *
 * Existing layoutPolarArc fans LATERAL (right of parent) — correct for
 * consult_peers lateral fans. Spine continuation needs a DIFFERENT
 * axis — children below the parent. This helper centers on
 * (anchorX, anchorY) and sweeps the lower half-plane.
 *
 * Symmetric: arc spans PEER_ARC_SPAN_DEG centered on 90 deg (straight
 * down). N=1 lands at (anchorX, anchorY+r); N=N spreads ±arcSpan/2.
 */
export function layoutPolarArcDownward(
  N: number, radius: number, anchorX: number, anchorY: number,
): Array<{ x: number; y: number }> {
  if (N <= 0) return [];
  if (N === 1) return [{ x: anchorX, y: anchorY + radius }];

  const arcSpan = CONSTANTS.PEER_ARC_SPAN_DEG;
  const startDeg = 90 - arcSpan / 2;
  const endDeg = 90 + arcSpan / 2;
  const positions: Array<{ x: number; y: number }> = [];

  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    const deg = startDeg + t * (endDeg - startDeg);
    const rad = (deg * Math.PI) / 180;
    positions.push({
      x: anchorX + radius * Math.cos(rad),
      y: anchorY + radius * Math.sin(rad),
    });
  }
  return positions;
}

/**
 * layoutLateralSubtree — NEW (v0.2) — positions a lateral fan +
 * returns bottomY so the spine cursor can clear it.
 *
 * Baseline chatDagLayout (~lines 510-525) handled this inline via
 * `arcBottom = Math.max(...fanNodes.map(...))` + a rowHeight calc.
 * The Phase 3 walker dispatches the math here; the caller
 * (walkChildrenFrom) advances `belowY = max(parent.h, lateralBottomY)
 * + GAP` instead of `belowY = parent.h + GAP`.
 *
 * Without this, a tool_call with multi-row peer fan extending below
 * its parent height could cause the spine continuation to land
 * INSIDE the lateral subtree (architect catch on spec triad 2).
 */
export function layoutLateralSubtree(
  laterals: Node[],
  anchorX: number,
  anchorY: number,
  parentHeight: number,
  dimsOf: (n: Node) => { w: number; h: number },
): number {
  if (laterals.length === 0) return anchorY + parentHeight;

  let cursorY = anchorY;
  let bottomY = anchorY + parentHeight;

  for (const l of laterals) {
    const { h } = dimsOf(l);
    l.position = { x: anchorX, y: cursorY };
    cursorY += h + CONSTANTS.TOOL_VERTICAL_GAP;
    bottomY = Math.max(bottomY, l.position.y + h);
  }

  return bottomY;
}
